import { Component, Input, OnInit } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { EState, ModuleView, ColorInfo } from './../current-agent-states.models';
import { CurrentAgentStatesService } from './../current-agent-states.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';

@Component({
  selector: 'cv-current-agent-states-cxone',
  templateUrl: './current-agent-states-cxone.component.html',
  styleUrl: './current-agent-states-cxone.component.scss'
})

// This directive is the parent of CurrentAgentStatesTable and CurrentAgentStatesGraph.
// This parent is in charge of loading data from the endpoint, saving and restoring state objects from the moduleManager, and coordinating actions with the children.
// Communication between the parent <--> children is done through Angular binding and @input decorators.
// --
// This directive broadcasts the following events:
//   construct-column-menu-item: Tells the child table directive to emit an object for the columns menu.
//   current-agents-data-changed: Tells the child directives that the data has changed and they should update themselves
//   filter-button-clicked: Tells the child table directive to show or hide the filter input box
//   table-state-restored: Tells the child to apply restored (ag-grid) state
//   _colorMapChanged: tells both the graph and table to refresh to reset the colors

export class CurrentAgentStatesCxoneComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    _filterBarShowing = false; // to toggle the filter bar on and off
    _activeView = ModuleView.Table; // This module has multiple views: table, chart, and dual.
    _dataLoading = false; // Used to prevent multiple outstanding data fetches.
    _filterHeaderItemShowing = false; // Used to mark if the magnifying glass filter icon is showing or not. will not show when in chart view
    _isFirstLoad = true; // Used for module logging.  Tracks if the module has loaded data for the first time.
    _stateSaveTimer: NodeJS.Timeout = null; // Used to debounce the state save of this directive.  Table directive debounces table state save, this directive only debounces the view.
    _aborter:  Promise<void> = null; // Used to abort the load if necessary
    abortLoad: () => void = null; // got from gauge.ts
    eStateEnum = EState; // Used like a C# Enum
    dataLoadError = false; // Not currently used, but can be tied to some indicator that the data is stale.
    localizationsLoaded = false; // Used in the template ng-if directives to construct the children directives only when localizations are ready
    noData = false;
    _stateToColorMap: ColorInfo;// Custom States custom colors mappings. Key is the StateDisplayName of the color, value is the hex color code.
    sharedData = { RowValues: [], IncompatibleSettings: false, MetricsAgentStatesColumnValues: [] }; // Current Agent State endpoint data, shared to the children directives.
    _loadingStates = {firstGridData: true, userRefresh: false };
    dataLoadSuccess = false;

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private service: CurrentAgentStatesService,
    ) {}

    ngOnInit(): void {
        // Module Manager things:
        this.moduleManager.showLoader = true;
        this.loadLocalizations();

        // Setup with ModuleManager:
        this.moduleManager.setOptions({
            onSettingsApplied: settings => {
                if (settings && settings.CustomStateColors) {
                    this._stateToColorMap = settings.CustomStateColors;
                }
                this.setLoadingState("userRefresh", true);
                this.loadData(false);
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.VeryShort,
            onRefresh: (isAutoRefresh) => {
                if (!isAutoRefresh)
                    this.setLoadingState("userRefresh", true);
                this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this._dataLoading,
        });

        this.moduleManager.addState({
            name: "view",
            getStateData: () => this._activeView,
            onStateLoaded: stateData => {
                if (typeof stateData === "string") {
                    this.changeView(stateData, true);
                } else {
                    console.warn("Current agent states module state load got invalid view value:", stateData, this._activeView
                    + " will be used");
                }
            },
            autoLoad: true,
        });

        if (this._activeView === ModuleView.Table || this._activeView === ModuleView.Dual) {
            this._filterHeaderItemShowing = true;
            this.addSearch(this._activeView);
        }
    }

    addSearch(activeView: string) {
        if (activeView === ModuleView.Table || activeView === ModuleView.Dual) {
            this.moduleManager.addHeaderItem({
                icon: "search",
                click: () => this._filterBarShowing = !this._filterBarShowing,
                name: "searchIcon"
            });
        }
    }

    // this gets called when the view changes from graph table or dual
    toggleHeaderFilterButton(activeView: string) {
        if (activeView === ModuleView.Chart) {
            delete this.moduleManager.additionalHeaderItems["searchIcon"];
            this._filterHeaderItemShowing = false;
        }
        else {
            this.moduleManager.addHeaderItem({
                icon: "search",
                click: () => this._filterBarShowing = !this._filterBarShowing,
                name: "searchIcon"
            });
            this._filterHeaderItemShowing = true;
        }
    }

    /**
     * This function is used to update _loadingStates.  It also shows the module manager's loader, if ctrl.isLoading() returns true.
     *
     * @param subState String name of the property of _loadingStates to change. TYPE STRING
     * @param isLoading boolean value to set above subState to. TYPE BOOLEAN
     */
    setLoadingState(subState, isLoading) {
        if (this._loadingStates[subState] !== undefined) {
            this._loadingStates[subState] = !!isLoading;
        }
        // Loading spinner:
        this.moduleManager.showLoader = this.isLoading();
    }

    /**
     * This function returns a boolean indicating whether any of the loadingStates are set to true.
     */
    isLoading() {
        const isLoading = Object.keys(this._loadingStates).reduce(
            (accumulator, subState) => accumulator || this._loadingStates[subState], false);
        return isLoading;
    };

    /**
     * This function is used to return the current view.
     */
    getActiveView() { return this._activeView; };

    /**
     * Loads the data for the two children directives.
     */
    async loadData(isAutoRefresh: boolean) {
        if (this._dataLoading) { // cancel current load
            this.abortLoad?.();
            this.abortLoad = null;
        }

        this._dataLoading = true;
        const { UseMetrics } = this.moduleManager.customSettings;
        this._aborter = new Promise<void>((resolve) => this.abortLoad = () => resolve());
        let data = null;
        try {
            data = await this.service.loadData(
                this.moduleKey, isAutoRefresh, this._isFirstLoad, this._aborter, true, UseMetrics.getValue());
        }
        catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
            return;
        }
        finally {
            this._dataLoading = false;
        }

        // need to have a new array reference in order for ngOnChanges to be called
        this.sharedData = { RowValues: [], IncompatibleSettings: false, MetricsAgentStatesColumnValues: [] };

        
        this.sharedData = {
            RowValues: data.RowValues,
            IncompatibleSettings: data.IncompatibleSettings,
            MetricsAgentStatesColumnValues: data.MetricsAgentStatesColumnValues
        };
        this.noData = this.sharedData.RowValues.length === 0;
        this.dataLoadSuccess = !this.noData;
        this.dataLoadError = false;
        this._isFirstLoad = false;
        this.moduleManager.setAutoRefreshReady();
        this.setLoadingState("userRefresh", false);
        this.setLoadingState("firstGridData", false);
        this.addModuleDisplayMenuItem();

        this.moduleManager.moduleOptions.nameSuffix = ` (${this.sharedData?.RowValues?.length || 0})`;
    };

    // Adds the menu items for the different views.  Should be in a separate function so it can be reconstructed when the view changes.
    addModuleDisplayMenuItem() {
        this.moduleManager.addMenuItem({
            name: this.cvLocalizationService.getCachedText("Current_Agent_States_Module", "Widget_Display", "Widget Display"),
            icon: "chartType",
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => true,
            subItems: [{
                name: this.cvLocalizationService.getCachedText("Current_Agent_States_Module" ,"Display_Table", "Table"),
                icon: "tableView",
                click: () => { this.changeView(ModuleView.Table);
                    this.toggleHeaderFilterButton(this._activeView);
                },
            },
            {
                name: this.cvLocalizationService.getCachedText("Current_Agent_States_Module" ,"Display_Chart", "Chart"),
                icon: "chartView",
                click: () => { this.changeView(ModuleView.Chart);
                    this.toggleHeaderFilterButton(this._activeView);
                },
            },
            {
                name: this.cvLocalizationService.getCachedText("Current_Agent_States_Module","Display_Dual", "Table and Chart"),
                icon: "tableAndChartView",
                click: () => { this.changeView(ModuleView.Dual);
                    this.toggleHeaderFilterButton(this._activeView);
                },
            }],
            readOnlyEnabled: true,
        } as ModuleManagerSubMenu);
    }

    /**
     * This function changes the view to the requested value, if valid.
     *
     * @param view View to change to TYPE: STRING
     * @param ignorePublish If true, we will not request a publish (used for state load) TYPE: BOOLEAN
     *
     */
    changeView(view, ignorePublish = false) {
        if (this._activeView !== view) {
            this._activeView = view;
            this.moduleManager.resetMenu();
            this.addModuleDisplayMenuItem();
            this.queueStateSave();
            if (!ignorePublish)
                this.moduleManager.requestPublishNeeded();
        }
    }

    /**
     * This function debounces calls to ctrl.moduleManager.saveState("view")
     */
    queueStateSave() {
        // The parent only needs to directly save the module's active view.
        if (this._stateSaveTimer) {
            clearTimeout(this._stateSaveTimer);
        }

        this._stateSaveTimer = setTimeout(() => {
            this._stateSaveTimer = null;
            this.moduleManager.saveState("view");
        }, 500);
    }

    private async loadLocalizations() {
        await this.cvLocalizationService.GetSection("Current_Agent_States_Module");
        this.localizationsLoaded = true;
    }
}
